'use es6';

import PropTypes from 'prop-types';
import { createRef, Component } from 'react';
import debounce from 'react-utils/debounce';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UILink from 'UIComponents/link/UILink';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UIExpandingTextArea from 'UIComponents/input/UIExpandingTextArea';
import UIButton from 'UIComponents/button/UIButton';
import H2 from 'UIComponents/elements/headings/H2';
import CloseableTipAlert from './CloseableTipAlert';
import EmbedLivePreview from './embed/EmbedLivePreview';
import { i18nKey } from '../utils/embed';
import { EmbedFormats, EmbedReadMoreLink, ErrorTypes, LoadingStatus } from '../constants/embed';
import { getEmbedType, defaultValidateEmbed, sanitizeEmbedHtml } from '../utils/embedSrc';
import { DEBOUNCE_TIMEOUT } from '../constants/toolbar';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class EmbedModal extends Component {
  constructor(props) {
    super(props);
    this.handleSubmit = () => {
      const {
        onSubmit,
        onClose
      } = this.props;
      const {
        previewData
      } = this.state;
      onSubmit(previewData);
      return onClose();
    };
    this.handleChange = ({
      target: {
        value
      }
    }) => {
      const trimmedValue = sanitizeEmbedHtml(value);
      let nextState = {};
      const type = getEmbedType(trimmedValue);
      if (type === EmbedFormats.URL) {
        this.debouncedFetchEmbedCode({
          url: trimmedValue
        });
      } else if (type === EmbedFormats.EMBED || type === EmbedFormats.GENERIC_EMBED) {
        this.debouncedFetchEmbedCode({
          html: trimmedValue
        }, type);
      } else {
        nextState = {
          previewLoadingStatus: LoadingStatus.NONE,
          previewData: {},
          error: trimmedValue ? ErrorTypes.GENERAL : ErrorTypes.NONE
        };
      }
      this.setState(Object.assign({
        embed: value,
        error: ErrorTypes.NONE,
        urlPasteMatch: false
      }, nextState));
    };
    this.fetchEmbedCode = (data, inputType) => {
      this.setState({
        previewLoadingStatus: LoadingStatus.LOADING
      });
      this.props.fetchEmbedCode(data).then(result => {
        const {
          previewData
        } = this.state;
        if (inputType === EmbedFormats.GENERIC_EMBED) {
          this.setState({
            previewLoadingStatus: LoadingStatus.NONE,
            previewData: result,
            error: ErrorTypes.EMBED_CODE
          });
        } else if (JSON.stringify(previewData) === JSON.stringify(result)) {
          this.setState({
            previewLoadingStatus: LoadingStatus.COMPLETE
          });
        } else {
          // Only update state if the data changed
          this.setState({
            previewData: result,
            urlPasteMatch: result.source === 'url'
          });
        }
      }).catch(err => {
        setTimeout(() => {
          // Throw an async error for app-level sentries
          throw new Error(err);
        });
        this.setState({
          previewLoadingStatus: LoadingStatus.NONE,
          previewData: {},
          error: ErrorTypes.FETCH,
          urlPasteMatch: false
        });
      });
    };
    this.handleSetContentDone = () => {
      this.setState({
        previewLoadingStatus: LoadingStatus.COMPLETE
      });
    };
    this.canInsert = () => {
      const {
        embed,
        previewLoadingStatus,
        error
      } = this.state;

      // Allow submission if there is no error and the preview is loaded
      // or if its a generic, possibly custom, embed code
      const canInsert = embed && (previewLoadingStatus === LoadingStatus.COMPLETE && error === ErrorTypes.NONE || error === ErrorTypes.EMBED_CODE);
      return canInsert;
    };
    this.state = {
      embed: props.embed,
      error: ErrorTypes.NONE,
      urlPasteMatch: false,
      previewLoadingStatus: LoadingStatus.NONE,
      previewData: {}
    };
    this.debouncedFetchEmbedCode = debounce(this.fetchEmbedCode, DEBOUNCE_TIMEOUT);
    this.embedInputRef = /*#__PURE__*/createRef();
  }
  renderUrlPasteMatch() {
    if (!this.state.urlPasteMatch) {
      return null;
    }
    const {
      getUserAttributes,
      setUserAttribute
    } = this.props;
    return /*#__PURE__*/_jsxs(CloseableTipAlert, {
      title: I18n.text(i18nKey('urlPasteTipTitle')),
      getUserAttributes: getUserAttributes,
      setUserAttribute: setUserAttribute,
      attributeKey: "embedUrlPastingTip",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('urlPastingTipText')
      }), /*#__PURE__*/_jsx(UILink, {
        href: `${EmbedReadMoreLink}#providers`,
        target: "_blank",
        children: I18n.text(i18nKey('urlPastingTipOembedLink'))
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('urlPastingTipText2')
      })]
    });
  }
  renderPreview() {
    const {
      previewLoadingStatus,
      previewData,
      error
    } = this.state;
    return /*#__PURE__*/_jsxs("div", {
      children: [previewLoadingStatus !== LoadingStatus.NONE && /*#__PURE__*/_jsx(UIFormLabel, {
        className: "m-top-2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('previewLabel')
        })
      }), previewLoadingStatus === LoadingStatus.LOADING && /*#__PURE__*/_jsx(UILoadingSpinner, {
        grow: true,
        size: "medium"
      }), /*#__PURE__*/_jsx(EmbedLivePreview, {
        showPreview: previewLoadingStatus === LoadingStatus.COMPLETE,
        data: error === ErrorTypes.NONE ? previewData : {},
        onSetContentDone: this.handleSetContentDone
      })]
    });
  }
  render() {
    const {
      onClose,
      rootNode,
      className,
      onCloseComplete
    } = this.props;
    const {
      embed,
      error
    } = this.state;
    let validationMessage = '';
    if (error !== ErrorTypes.NONE) {
      validationMessage = /*#__PURE__*/_jsx(FormattedMessage, {
        "data-test-id": "hs-content-embed-validation-message",
        message: i18nKey(`errors.${error}`)
      });
    }
    return /*#__PURE__*/_jsxs(UIModal, {
      width: 550,
      rootNode: rootNode,
      className: className,
      onCloseComplete: onCloseComplete,
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: onClose
        }), /*#__PURE__*/_jsx(H2, {
          children: I18n.text(i18nKey('modalTitle'))
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('embedInsertInstructions')
        }), /*#__PURE__*/_jsx(UIButton, {
          use: "link",
          external: true,
          href: EmbedReadMoreLink,
          children: `${I18n.text(i18nKey('learnMore'))}`
        }), /*#__PURE__*/_jsx(UIFormControl, {
          htmlFor: "embed_txtarea",
          label: I18n.text(i18nKey('embedModalInputLabel')),
          error: error !== ErrorTypes.NONE,
          validationMessage: validationMessage,
          className: "m-top-2",
          children: /*#__PURE__*/_jsx(UIExpandingTextArea, {
            autoFocus: true,
            "data-gramm": false,
            defaultValue: embed,
            inputRef: this.embedInputRef,
            name: "embed_txtarea",
            maxHeight: 160,
            shrink: true,
            value: embed,
            onChange: this.handleChange
          })
        }), this.renderUrlPasteMatch(), this.renderPreview()]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UIButton, {
          use: "primary",
          disabled: !this.canInsert(),
          onClick: this.handleSubmit,
          children: I18n.text(i18nKey('insert'))
        }), /*#__PURE__*/_jsx(UIButton, {
          use: "secondary",
          onClick: onClose,
          children: I18n.text(i18nKey('cancel'))
        })]
      })]
    });
  }
}
EmbedModal.propTypes = {
  embed: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  rootNode: PropTypes.object,
  className: PropTypes.string,
  onCloseComplete: PropTypes.func,
  fetchEmbedCode: PropTypes.func,
  getUserAttributes: PropTypes.func,
  setUserAttribute: PropTypes.func
};
EmbedModal.defaultProps = {
  embed: '',
  validateEmbed: defaultValidateEmbed
};